import { msg } from '@lingui/macro'

import { useWaiterRouter } from '@segments/Routing/client'
import btoa from 'btoa'
import { usePathname } from 'next/navigation'
import * as React from 'react'

import { TFOrderSubmitResult, TFPaymentGatewayUrls } from '~graphql/generated/clientGraphql'
import { useAuthCtx } from '~logic/auth/AuthProvider'
import { useTrans } from '~logic/i18n/useTrans'
import { useSubmitErrors } from '~shared/helpers/useSubmitErrors'
import { devLog } from '~tools/devLog'

const GATE_NOT_AVAILABLE = msg`The payment gate is not available right now.`

type THandleOnlinePaymentOptions = {
  orderId: ID
  gatewayUrls: TFPaymentGatewayUrls
}
/**
 * @Returns submit & payment handlers:
 * - `handleSubmit`: calls the passed submit order mutation, returns the submit result,
 * - `onSubmitFailed`: can be used to handle the possible submit errors,
 * - `handleOnlinePayment`: gets submit result, navigates to the payment gateway,
 * - `handlePostPayment`: navigates to the SubmittedScreen.
 */
export const useOrderSubmitHandlers = () => {
  const { trans } = useTrans()
  const { onSubmitFailed } = useSubmitErrors()

  const router = useWaiterRouter()
  const pathname = usePathname()
  const { authToken } = useAuthCtx()

  /**
   * if some of the pre-pay methods is selected, check if the URL is available & navigate into the gateway
   */
  const navigateToGateway = React.useCallback(
    ({ gatewayUrls, orderId }: THandleOnlinePaymentOptions) => {
      // if some pre-paid method was selected, we should get URL of the associated online payment gate
      //  GOPAY
      //  - https://help.gopay.com/cs/tema/integrace-platebni-brany/technicky-popis-integrace-platebni-brany/postup-integrace-platebni-brany
      //  - https://doc.gopay.cz/#redirect
      //
      //  CSOB
      //  - https://github.com/csob/paymentgateway
      //  - https://github.com/csob/paymentgateway/wiki
      //  ! during development close devtools (chrome) before being redirected to the gateway
      //  ! with devtools open, your request will be rejected
      //  https://github.com/csob/paymentgateway/issues/609#issuecomment-992544427
      //
      //  GP WEBPAY
      //  - https://developer.globalpay.com/resources/test-card-numbers
      //
      //  PAY FAST
      //  - https://developers.payfast.co.za/docs#step_1_form_fields
      //
      //  PAY U
      //  - https://developers.payu.com/en/restapi.html
      const {
        payUPaymentGatewayUrl,
        CSOBPaymentGatewayUrl,
        goPayPaymentGatewayUrl,
        payFastPaymentGatewayUrl,
        globalPaymentsPaymentGatewayUrl,
      } = gatewayUrls

      const gatewayUrl =
        CSOBPaymentGatewayUrl ??
        globalPaymentsPaymentGatewayUrl ??
        goPayPaymentGatewayUrl ??
        payFastPaymentGatewayUrl ??
        payUPaymentGatewayUrl

      if (!gatewayUrl) {
        onSubmitFailed([trans(GATE_NOT_AVAILABLE)], true)

        return
      }

      if (authToken !== null) {
        // On localhost BE doesn't recognize this new app from the previous 'doveze' app.
        // We can't change referer headers (https://developer.mozilla.org/en-US/docs/Glossary/Forbidden_header_name).
        // Therefore when returning from the payment gateway, we are redirected to the old app.
        // For convenience, the link to get back to this app is logged to the console in development mode.

        const hasNoParamsInUrl = router.screenRoute === 'form'
        const urlParams = `/?orderId=${orderId}&userToken=${btoa(authToken)}`

        devLog(
          'You can use this link to continue within the "speedlo-next" shop app flow later: ',
          `http://localhost:3000/${pathname.replace(router.screenRoute, 'validate-payment')}${
            hasNoParamsInUrl ? urlParams : ``
          }`,
        )
      }

      window.location.replace(gatewayUrl)
    },
    [authToken, onSubmitFailed, pathname, router.screenRoute, trans],
  )

  /**
   * post-paid payment methods are handled by the staff so just redirect user to the final screen
   */
  const navigateToSubmittedScreen = React.useCallback(
    (orderId: ID) => {
      if (!authToken) {
        // authToken should be always defined because we needed the token to create the order (just TS check)
        return
      }

      router.push(`order-placed`, {
        urlParams: {
          orderId,
          userToken: btoa(authToken),
        },
      })
    },
    [authToken, router],
  )

  /**
   * - checks if the mutation result is defined
   * - returns the `gatewayUrls`
   */
  const getUrlsFromResult = React.useCallback(
    (result: Nullable<TFOrderSubmitResult>) => {
      if (!result) {
        onSubmitFailed()
        return null
      }

      const { validationErrors, ...gatewayUrls } = result

      if (validationErrors.length > 0) {
        // BE validation failed – user forgot to eg. fill in the phone & FE somehow didn’t catch that
        onSubmitFailed(validationErrors)
        return null
      }

      return gatewayUrls
    },
    [onSubmitFailed],
  )

  return { navigateToGateway, navigateToSubmittedScreen, getUrlsFromResult, onSubmitFailed }
}
